import { getCrmAdvancedGradientTheme, getCrmAdvancedSectionPalette, normalizeCrmAdvancedSettings } from '@/lib/crm-advanced-site'
import { resolveCrmCustomGradientCss } from '@/lib/crm-custom-site-public'
import { getCrmPublicTemplatePalette } from '@/lib/crm-public-palettes'
import type { NoOrderSiteTheme } from '../../shared/crm-no-order-needed.js'

const safeHex = (value: unknown) => /^#[\da-f]{6}$/i.test(String(value || '')) ? String(value) : ''
const luminance = (hex: string) => {
  const channels = hex.slice(1).match(/../g)!.map((part) => {
    const value = parseInt(part, 16) / 255
    return value <= .04045 ? value / 12.92 : ((value + .055) / 1.055) ** 2.4
  })
  return channels[0] * .2126 + channels[1] * .7152 + channels[2] * .0722
}
const readableOn = (backgrounds: string[], dark: string) => {
  const contrast = (foreground: string, background: string) => {
    const left = luminance(foreground), right = luminance(background)
    return (Math.max(left, right) + .05) / (Math.min(left, right) + .05)
  }
  const minimum = (foreground: string) => Math.min(...backgrounds.map((background) => contrast(foreground, background)))
  return minimum('#ffffff') >= minimum(dark) ? '#ffffff' : dark
}

/** Display-only inheritance: never copy theme colors into saved page/mail settings. */
export const resolveCrmNoOrderTheme = (value?: NoOrderSiteTheme | null) => {
  const template = getCrmPublicTemplatePalette(value?.templateKey)
  const primary = safeHex(value?.accentColor) || (value?.mode === 'custom' ? '#2563eb' : template.primary)
  const advanced = normalizeCrmAdvancedSettings({ accentColor: primary, themeGradientKey: value?.gradientKey }, 'homepage', value?.siteId || '')
  // Custom websites follow their global style, including an explicit no-gradient choice.
  const gradientKey = value?.mode === 'custom' ? value.gradientKey : advanced.themeGradientKey
  const gradient = getCrmAdvancedGradientTheme(gradientKey)
  const css = resolveCrmCustomGradientCss(gradientKey, primary)
  const palette = getCrmAdvancedSectionPalette(gradientKey, primary)
  return {
    gradient: css.gradient, primary: css.primary, soft: palette.bgSoft, alternate: palette.bgAlt,
    deep: palette.bgDeep, title: palette.textOnWhite,
    text: `color-mix(in srgb, ${css.primary} 55%, #334155)`,
    onAccent: readableOn(gradient ? [gradient.from, gradient.to] : [primary], '#0f172a'),
    onDeep: readableOn([palette.bgDeep], '#0f172a'),
  }
}
